import type {
  ChangePlanningStageBody,
  PlanningProjectDto,
  UpsertPlanningProjectBody
} from '@malash-office/types';
import instanceAxios from '@/util/axios';
import type {
  PlanningProjectCardResponse,
  PlanningProjectListParams,
  PlanningProjectListResponse,
  PlanningSummaryResponse
} from './planning.types';

/** The project itself: the table, the KPI header, the card, and the two commands on it. */

export const PLANNING_BASE = 'pvt/planning';

/** Drop empty/undefined params so they don't pollute the query string. */
export const cleanParams = <T extends object>(params: T): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') out[key] = String(value);
  });
  return out;
};

export const fetchPlanningProjects = async (
  params: PlanningProjectListParams
): Promise<PlanningProjectListResponse> => {
  const { data } = await instanceAxios.get<PlanningProjectListResponse>(`${PLANNING_BASE}/projects`, {
    params: cleanParams(params)
  });
  return data;
};

/** The five tiles and the attention panel. Declared before `/projects/:id` on the server too. */
export const fetchPlanningSummary = async (): Promise<PlanningSummaryResponse> => {
  const { data } = await instanceAxios.get<PlanningSummaryResponse>(`${PLANNING_BASE}/summary`);
  return data;
};

export const fetchPlanningProject = async (id: number): Promise<PlanningProjectCardResponse> => {
  const { data } = await instanceAxios.get<PlanningProjectCardResponse>(`${PLANNING_BASE}/projects/${id}`);
  return data;
};

/**
 * The type decides what else the body must carry — `upgrade`/`redesign` need a `productId`,
 * `external` needs a `productionCustomerId` (`requiresCatalogProduct` / `requiresProductionCustomer`).
 * The server enforces both in Joi and again in the service, because the type stays editable.
 */
export const createPlanningProject = async (body: UpsertPlanningProjectBody): Promise<PlanningProjectDto> => {
  const { data } = await instanceAxios.post<PlanningProjectDto>(`${PLANNING_BASE}/projects`, body);
  return data;
};

export const updatePlanningProject = async (
  id: number,
  body: Partial<UpsertPlanningProjectBody>
): Promise<PlanningProjectDto> => {
  const { data } = await instanceAxios.patch<PlanningProjectDto>(`${PLANNING_BASE}/projects/${id}`, body);
  return data;
};

/**
 * The header dropdown's write — a command, not `PATCH { stage }`.
 *
 * It carries one of the two axes and never both (see `productPlanning.core.ts`), and the server
 * does three things with it that an edit would not: an audit row, a `kind: 'system'` chat message
 * so the Activity panel shows the move, and optionally the task that comes with the new stage.
 */
export const changePlanningStage = async (id: number, body: ChangePlanningStageBody): Promise<PlanningProjectDto> => {
  const { data } = await instanceAxios.post<PlanningProjectDto>(`${PLANNING_BASE}/projects/${id}/stage`, body);
  return data;
};

/**
 * Delete the project and everything filed under it — 204, and irreversible.
 *
 * Not the same as cancelling it. `changePlanningStage({ status: 'cancelled' })` is the soft exit
 * and keeps the code, the quotes and the chat; this removes a project that should never have
 * existed. The list screen offers both and the confirmation dialog names the difference.
 */
export const deletePlanningProject = async (id: number): Promise<void> => {
  await instanceAxios.delete(`${PLANNING_BASE}/projects/${id}`);
};
